import React, { useEffect, useState } from "react";
import { AppFrame, TopBar, BottomNav } from "@/components/Shell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, User } from "lucide-react";

export default function ManageStaff() {
  const [staff, setStaff] = useState([]);
  const [count, setCount] = useState({ count: 0, max: 10 });
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/staff"), api.get("/staff/count")]);
    setStaff(s.data);
    setCount(c.data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error("Email and password required");
    setSaving(true);
    try {
      await api.post("/staff", form);
      toast.success("Staff added");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this staff account?")) return;
    await api.delete(`/staff/${id}`);
    toast.success("Removed");
    load();
  };

  const full = count.count >= count.max;

  return (
    <AppFrame>
      <TopBar title="Team" subtitle={`${count.count} of ${count.max} used`} />
      <div className="px-5 mt-4 space-y-4">
        <form
          onSubmit={add}
          className="rounded-2xl p-3 space-y-2"
          style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
        >
          <div className="font-display text-[17px]" style={{ color: "var(--ink)" }}>Add staff</div>
          <input
            data-testid="staff-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name (optional)"
            className="w-full h-11 rounded-xl px-3 text-sm outline-none"
            style={{ border: "1px solid rgba(27,25,23,0.15)" }}
          />
          <input
            data-testid="staff-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="staff@example.com"
            className="w-full h-11 rounded-xl px-3 text-sm outline-none"
            style={{ border: "1px solid rgba(27,25,23,0.15)" }}
          />
          <input
            data-testid="staff-password"
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Password (min 6 chars)"
            className="w-full h-11 rounded-xl px-3 text-sm outline-none"
            style={{ border: "1px solid rgba(27,25,23,0.15)" }}
          />
          <button
            data-testid="staff-add-btn"
            type="submit"
            disabled={saving || full}
            className="w-full h-11 rounded-full text-[11px] tracking-editorial-sm uppercase press disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: "var(--ink)", color: "var(--bg)" }}
          >
            <Plus size={14} /> {full ? "Max staff reached" : "Add staff"}
          </button>
        </form>

        <div className="space-y-2">
          {staff.length === 0 ? (
            <div className="rounded-2xl py-10 text-center" style={{ background: "var(--bg-alt)", border: "1px solid rgba(27,25,23,0.06)" }}>
              <div className="font-display-italic text-lg">no staff yet</div>
              <p className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>Add helpers to speed up product uploads.</p>
            </div>
          ) : staff.map((s) => (
            <div
              key={s.id}
              data-testid={`staff-row-${s.id}`}
              className="flex items-center gap-3 rounded-2xl p-3"
              style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
            >
              <div className="w-10 h-10 rounded-full grid place-items-center" style={{ background: "var(--bg-alt)" }}>
                <User size={16} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{s.name || s.email.split("@")[0]}</div>
                <div className="text-[11px] truncate" style={{ color: "var(--ink-3)" }}>{s.email}</div>
              </div>
              <button
                data-testid={`staff-del-${s.id}`}
                onClick={() => del(s.id)}
                className="w-9 h-9 rounded-full grid place-items-center press"
                style={{ border: "1px solid rgba(27,25,23,0.12)", color: "var(--accent)" }}
              ><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
      <BottomNav />
    </AppFrame>
  );
}
